import { Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  Modal,
  TextField,
  Typography,
  type TypographyProps,
} from '@mui/material';
import { useEffect, useRef, useState, type ChangeEventHandler } from 'react';
import { Form } from '../../../../design';
import { EikenMaterialResource } from '../../../../resources';
import { SelectField } from './SelectField';

type Props = {
  open: boolean;
  onClose: () => void;
};

const DEFAULT_MASTER_PROMPT = `Please generate a new Eiken Grade 2 test including 

1. Test title
2. Pronunciation paragraph
3. Image text (used after You should start with the sentence)
4. Speech bubble content for the 3-panel image
5. Image generation prompt (for GPT-to-image model)
6. Questions 1–4

Follow the guide below to create the question.

英検二級面接試験 質問作成ガイド / Guide for Teachers to Create Eiken Grade 2 Interview Questions
1. パッセージを選ぶ / Choose or Write a Passage
Use a passage (about 30–50 words) on a familiar topic: "Volunteering," "School Events," "Recycling," etc.
It should be understandable and about topics relevant to teens or general life.

2. 内容確認の質問 / Comprehension Questions
Create TRUE/FALSE or factual questions directly from the passage.

Example:
"According to the passage, what do many students do after school?"

3. イラストに関する質問 / Questions About the Illustration
Show a picture related to the passage.
The tester should describe the 3-panel illustration from left to right following an assigned first sentence.

Possible illustration question:
You should start with the sentence. "One day, Lisa went to bed early in order to wake up early to go to the amusement park."

4. 意見を求める質問 / Opinion Questions
Ask the student to express their opinion about the topic.
Use "Do you think…?" or "What do you think about…?" or "Have you ever…?"
Encourage reasons/examples.

Example:
"Do you think recycling is important? Why or why not?"
"Have you ever helped in your community? Please explain."

5. 技術的なポイント / Tips for Teachers
Keep questions simple and clear.
Opinion questions should allow multiple answers (not just yes/no).
Try to relate content to students' experiences and interests.
Provide supporting questions, such as "Why?" or "Can you tell me more?"

具体例 / Example
Passage:
"Many people use the internet to find information. Students use it to do homework. However, some people think too much time online is not good."

Q1 (Comprehension):
Why do many people use the internet?

Q2 (About the illustration):
What is the girl doing in the picture?

Q3 (Opinion):
Do you think students should spend less time on the internet? Please tell me your opinion.

Q4 (Agree/Disagree)
In recent years, an increasing number of schools in Japan have stopped doing club activities during the holidays and on weekends. Do you think more schools will reduce club activity days in the future?`;

const DEFAULT_IMAGE_QA_PROMPT = `Three-panel manga style illustration about a school recycling event. 

Panel 1:
Classroom after school, Japanese teenage boy Ken holds a stack of used paper, smiling, speech bubble says: "Let's collect used paper and get everyone to join."

Panel 2:
School hallway with bulletin board, female friend holds colorful posters, speech bubble: "Great idea! We can make posters after class."

Panel 3:
Library entrance, friendly teacher holding an open book, speech bubble: "If we work together, we can buy new books for the library." Bright, cheerful colors, anime style.`;

export function EditAIPromptsModal({ open, onClose }: Props) {
  const fileInputref = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [gptModelImage, setGptModelImage] = useState<string>('');
  const [gptModelText, setGptModelText] = useState<string>('');
  const [masterPrompt, setMasterPrompt] = useState(DEFAULT_MASTER_PROMPT);
  const [imagePromptAttachments, setImagePromptAttachments] = useState<string[]>([]);
  const [imageQAPrompt, setImageQAPrompt] = useState(DEFAULT_IMAGE_QA_PROMPT);
  const [modelOptions, setModelOptions] = useState<string[]>([]);

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const files = Array.from(event.target.files ?? []);

    if (!files.length) return;

    setIsUploading(true);

    const newUrls: string[] = [];
    for (const file of files) {
      const url = await EikenMaterialResource.upload(file, 'image');
      newUrls.push(url);
    }

    setImagePromptAttachments((prev) => [...prev, ...newUrls]);
    setIsUploading(false);
  };

  const handleRemoveAttachment = (index: number) => {
    setImagePromptAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    await EikenMaterialResource.updateAIConfig({
      gptModelImage,
      gptModelText,
      masterPrompt,
      imagePromptAttachments,
      imageQAPrompt,
    });
    setIsLoading(false);
    onClose();
  };

  useEffect(() => {
    EikenMaterialResource.fetchAIConfig().then((config) => {
      setGptModelImage(config.gptModelImage || 'gpt-4o');
      setGptModelText(config.gptModelText || 'o3');
      setMasterPrompt(config.masterPrompt || DEFAULT_MASTER_PROMPT);
      setImagePromptAttachments(config.imagePromptAttachments || []);
      setImageQAPrompt(config.imageQAPrompt || DEFAULT_IMAGE_QA_PROMPT);
    });

    EikenMaterialResource.getAIModels().then((models) => {
      setModelOptions(models);
    });
  }, []);

  return (
    <Modal open={open} onClose={onClose}>
      <Box>
        <Form
          onSubmit={handleSubmit}
          className="bg-white absolute max-h-[90vh] overflow-y-auto max-w-[700px] top-1/2 left-1/2 w-full transform -translate-x-1/2 -translate-y-1/2 p-6 gap-6 flex flex-col rounded-lg"
        >
          <Box className="flex items-center justify-between">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Edit AI Prompts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Edit the prompts used to generate new questions for tests
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Box className="bg-gray-50 p-6 rounded-lg grid grid-cols-2 gap-6">
            <FormControl className="flex flex-col gap-2">
              <Label htmlFor="gptModelImage">GPT Model (Image)</Label>
              <SelectField
                id="gptModelImage"
                name="gptModelImage"
                size="small"
                value={gptModelImage}
                onChange={(e) => setGptModelImage(e.target.value)}
                options={modelOptions}
                placeholder="Select model"
              />
            </FormControl>
            <FormControl className="flex flex-col gap-2">
              <Label htmlFor="gptModelText">GPT Model (Text)</Label>
              <SelectField
                id="gptModelText"
                name="gptModelText"
                size="small"
                value={gptModelText}
                onChange={(e) => setGptModelText(e.target.value)}
                options={modelOptions}
                placeholder="Select model"
              />
            </FormControl>
            <FormControl className="col-span-2 flex flex-col gap-2">
              <Label htmlFor="masterPrompt">Master Prompt to Generate Prompts</Label>
              <TextField
                id="masterPrompt"
                name="masterPrompt"
                size="small"
                multiline
                rows={4}
                placeholder="Enter master prompt to generate prompts"
                className="bg-white"
                value={masterPrompt}
                onChange={(e) => setMasterPrompt(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      resize: 'vertical',
                    },
                  },
                }}
              />
            </FormControl>
            <FormControl className="col-span-2 flex flex-col gap-2">
              <Label htmlFor="imagePromptAttachments">
                Image Prompt Attachment <span className="font-normal">for Style Reference</span>
              </Label>
              <Box className="flex gap-4">
                <Box>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={() => fileInputref.current?.click()}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </Button>
                  <input
                    ref={fileInputref}
                    hidden
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />
                </Box>
                <Box className="grid grid-cols-3 gap-4 w-full">
                  {!imagePromptAttachments.length && (
                    <p className="p-2 col-span-3">No attachments uploaded</p>
                  )}

                  {imagePromptAttachments.map((url, index) => (
                    <Box key={url} className="relative">
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(index)}
                        className="absolute inset-0 w-full h-full bg-black/40 opacity-0 hover:opacity-100 flex items-start justify-end"
                      >
                        <Close className="text-white" />
                      </button>
                      <img
                        src={url}
                        alt={`attachment-${index}`}
                        className="w-full h-auto object-cover"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </FormControl>
            <FormControl className="col-span-2 flex flex-col gap-2">
              <Label htmlFor="imageQAPrompt">Image QA prompt</Label>
              <TextField
                id="imageQAPrompt"
                name="imageQAPrompt"
                size="small"
                multiline
                rows={4}
                placeholder="Enter image QA prompt"
                className="bg-white"
                value={imageQAPrompt}
                onChange={(e) => setImageQAPrompt(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    '& textarea': {
                      resize: 'vertical',
                    },
                  },
                }}
              />
            </FormControl>
          </Box>
          <Box className="flex justify-end w-full">
            <Button type="submit" variant="contained" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CircularProgress size={24} /> Saving...
                </>
              ) : (
                <>
                  <Check /> Save prompts
                </>
              )}
            </Button>
          </Box>
        </Form>
      </Box>
    </Modal>
  );
}

const Label = ({ sx, ...props }: TypographyProps<'label'>) => {
  return (
    <Typography variant="body2" component="label" sx={{ fontWeight: 600, ...sx }} {...props} />
  );
};
